<script setup>
import { computed } from 'vue'
import FormInput from '../Atoms/FormInput.vue'
import FormText from '../Atoms/FormText.vue'
import FormButton from '../Atoms/FormButton.vue'

const props = defineProps({
	platform: {
		type: Object,
		required: true,
	},
	index: {
		type: Number,
		default: 0,
	},
})

const emit = defineEmits(['meta-change', 'save', 'save-all', 'game-input'])

const editor = computed(() => props.platform?.serviceMetaEditor || {})
const form = computed(() => editor.value.form || {})
const changes = computed(() => editor.value.changes || {})
const processingState = computed(() => editor.value.formProcessing || {})

const isFbTimeline = computed(() => props.platform?.config?.type === 'userFBtimeline')
const editingLocked = computed(() => props.platform?.config?.postCreationMetaEditing === false)

const maxTitle = computed(() => props.platform?.maxLengthTitle || 100)
const maxDescription = computed(() => props.platform?.maxLengthDescription || 5000)

const has = (prop) => prop in form.value

const isProcessing = computed(() =>
	['title', 'description', 'game'].some((p) => processingState.value[p])
)

const changedProps = computed(() =>
	['title', 'description', 'game'].filter((p) => has(p) && changes.value[p])
)
const hasChanges = computed(() => changedProps.value.length > 0)

// "Save to all platforms" only applies to the broadcast platforms (FB/YT/Twitch)
const canSaveToAll = computed(() => has('title') || has('description'))

const onChange = (prop) => emit('meta-change', prop)
</script>

<template>
	<div class="w-full bg-surface-1/50 md:bg-surface-2 rounded-lg px-0 py-2 md:p-4 mt-2 md:mt-3">
		<div class="flex flex-col gap-y-3 md:gap-y-4">
			<!-- Facebook doesn't let us update a live timeline video; the backend applies these when it creates the next one -->
			<p v-if="isFbTimeline" class="text-tiny text-surface-7 mb-0 leading-relaxed">
				Changes to a Facebook Timeline stream apply from your next broadcast.
			</p>

			<!-- Stream title -->
			<div v-if="has('title')">
				<div class="flex items-center justify-between gap-2 mb-1.5">
					<label class="text-tiny font-medium text-surface-9 mb-0">Stream title</label>
					<span class="text-tiny text-surface-6 tabular-nums">{{ (form.title || '').length }}/{{ maxTitle }}</span>
				</div>
				<FormInput v-model="platform.serviceMetaEditor.form.title" :maxlength="maxTitle"
					:disabled="editingLocked || processingState.title" placeholder="Add a title for this broadcast"
					@change="onChange('title')" />
			</div>

			<!-- Description -->
			<div v-if="has('description')">
				<div class="flex items-center justify-between gap-2 mb-1.5">
					<label class="text-tiny font-medium text-surface-9 mb-0">Description</label>
					<span class="text-tiny text-surface-6 tabular-nums">{{ (form.description || '').length }}/{{ maxDescription
					}}</span>
				</div>
				<FormText v-model="platform.serviceMetaEditor.form.description" :maxlength="maxDescription"
					:disabled="editingLocked || processingState.description" rows="3" placeholder="Add a description (optional)"
					@change="onChange('description')" />
			</div>

			<!-- Category / game (Twitch) -->
			<div v-if="has('game')">
				<label class="text-tiny font-medium text-surface-9 mb-1.5 block">Category</label>
				<FormInput v-model="platform.serviceMetaEditor.form.game" placeholder="Enter a category or game"
					@input="emit('game-input', $event)" @change="onChange('game')" />
			</div>

			<!-- Actions -->
			<div class="flex flex-wrap items-center gap-2 pt-1">
				<FormButton class="w-full md:w-auto justify-center" size="md" :disabled="!hasChanges || isProcessing"
					@click="emit('save')">
					{{ isProcessing ? 'Saving…' : 'Save changes' }}
				</FormButton>
				<FormButton v-if="canSaveToAll" class="w-full md:w-auto justify-center" size="md" type="secondary"
					:disabled="!hasChanges || isProcessing" @click="emit('save-all')">
					Save to all platforms
				</FormButton>
				<span v-if="canSaveToAll"
					class="text-tiny text-surface-6 w-full text-center md:w-auto md:ml-auto md:text-left">Applies to
					Facebook, YouTube &amp; Twitch.</span>
			</div>
		</div>
	</div>
</template>
